import { createSlice } from "@reduxjs/toolkit";
import {
  approveVoucher,
  atcRequestQuote,
  atcRequestVoucher,
  denyRequest,
  filterRequests,
  filterUserRequests,
  getQuoteByAtc,
  getRequestsAtc,
  getRequestsByAtc,
  getRequestsUser,
  sendQuote,
  voucherFromQuote,
} from "../apiActions/requestsAction";
import { Exam } from "./examSlice";
import successModal from "../../components/ConfirmationModal";
import { notification } from "antd";
import { redCross, confirmTick } from "../../assets";

interface Atc {
  name: string;
  id: string;
  created_at: string;
}

export type RequestAtc = {
  id: string;
  created_at: string;
  invoice_number: string;
  payment_id: string;
  status: string;
  amount: number;
  number_of_vouchers: number;
  user: string | null;
  atc: Atc;
  exam: Exam;
  comment: string;
};

interface RequestState {
  loading: {
    getRequest: boolean;
    createExam: boolean;
    updateExam: boolean;
    deleteExam: boolean;
  };
  deleteLoading: boolean;
  error: null | string;
  requests: RequestAtc[];
  requestUser: RequestAtc[];
  modal: boolean;
}

const initialState: RequestState = {
  requests: [],
  requestUser: [],
  error: null,
  loading: {
    getRequest: false,
    updateExam: false,
    deleteExam: false,
    createExam: false,
  },
  deleteLoading: false,
  modal: false,
};

const examSlice = createSlice({
  name: "request",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getRequestsAtc.pending, (state: RequestState) => {
        state.loading.getRequest = true;
        state.error = null;
      })
      .addCase(getRequestsAtc.fulfilled, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = null;
        state.requests = action.payload;
        state.modal = false;
      })
      .addCase(getRequestsAtc.rejected, (state: RequestState, action) => {
        state.loading.getRequest = false;
      })
      .addCase(getRequestsByAtc.pending, (state: RequestState) => {
        state.loading.getRequest = true;
        state.error = null;
      })
      .addCase(getRequestsByAtc.fulfilled, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = null;
        state.requests = action.payload;
        state.modal = false;
      })
      .addCase(getRequestsByAtc.rejected, (state: RequestState, action) => {
        state.loading.getRequest = false;
      })
      .addCase(getQuoteByAtc.pending, (state: RequestState) => {
        state.loading.getRequest = true;
        state.error = null;
      })
      .addCase(getQuoteByAtc.fulfilled, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = null;
        state.requests = action.payload;
        state.modal = false;
      })
      .addCase(getQuoteByAtc.rejected, (state: RequestState, action) => {
        state.loading.getRequest = false;
      })
      .addCase(filterRequests.pending, (state: RequestState) => {
        state.loading.getRequest = true;
        state.error = null;
      })
      .addCase(filterRequests.fulfilled, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = null;
        state.requests = action.payload;
        state.modal = false;
      })
      .addCase(filterRequests.rejected, (state: RequestState, action) => {
        state.loading.getRequest = false;
      })
      .addCase(filterUserRequests.pending, (state: RequestState) => {
        state.loading.getRequest = true;
        state.error = null;
      })
      .addCase(filterUserRequests.fulfilled, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = null;
        state.requestUser = action.payload;
        state.modal = false;
      })
      .addCase(filterUserRequests.rejected, (state: RequestState, action) => {
        state.loading.getRequest = false;
      })
      .addCase(getRequestsUser.pending, (state: RequestState) => {
        state.loading.getRequest = true;
        state.error = null;
      })
      .addCase(getRequestsUser.fulfilled, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = null;
        state.requestUser = action.payload;
      })
      .addCase(getRequestsUser.rejected, (state: RequestState, action) => {
        state.loading.getRequest = false;
      })
      .addCase(approveVoucher.pending, (state: RequestState) => {
        state.loading.getRequest = true;
        state.error = null;
      })
      .addCase(approveVoucher.fulfilled, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = null;
        state.modal = true;
        successModal.show({ text: "Voucher Sent" });
      })
      .addCase(approveVoucher.rejected, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = action.payload as string;
        notification.error(action.payload as any);
      })
      .addCase(sendQuote.pending, (state: RequestState) => {
        state.loading.getRequest = true;
        state.error = null;
      })
      .addCase(sendQuote.fulfilled, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = null;
        successModal.show({ text: "Quotation Sent" });
      })
      .addCase(sendQuote.rejected, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = action.payload as string;
        notification.error(action.payload as any);
      })
      .addCase(denyRequest.pending, (state: RequestState) => {
        state.loading.getRequest = true;
        state.error = null;
      })
      .addCase(denyRequest.fulfilled, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = null;
        successModal.show({ image: redCross, text: "Request Denied" });
      })
      .addCase(denyRequest.rejected, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = action.payload as string;
        notification.error(action.payload as any);
      })
      .addCase(atcRequestVoucher.pending, (state: RequestState) => {
        state.loading.getRequest = true;
        state.error = null;
      })
      .addCase(atcRequestVoucher.fulfilled, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = null;
        successModal.show({ image: confirmTick, text: "Voucher Requested" });
      })
      .addCase(atcRequestVoucher.rejected, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = action.payload as string;
        notification.error(action.payload as any);
      })
      .addCase(atcRequestQuote.pending, (state: RequestState) => {
        state.loading.getRequest = true;
        state.error = null;
      })
      .addCase(atcRequestQuote.fulfilled, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = null;
        successModal.show({ image: confirmTick, text: "Quotation Requested" });
      })
      .addCase(atcRequestQuote.rejected, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = action.payload as string;
        notification.error(action.payload as any);
      })
      .addCase(voucherFromQuote.pending, (state: RequestState) => {
        state.loading.getRequest = true;
        state.error = null;
      })
      .addCase(voucherFromQuote.fulfilled, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = null;
        successModal.show({
          image: confirmTick,
          text: "Voucher From Quote Requested",
        });
      })
      .addCase(voucherFromQuote.rejected, (state: RequestState, action) => {
        state.loading.getRequest = false;
        state.error = action.payload as string;
        notification.error(action.payload as any);
      });
  },
});

// export const {} = examSlice.actions;
export default examSlice.reducer;
